import api from "@/config/api";
import { handleApiError } from "@/utils/ErrorHandler";
import { createPaginationModule, defaultPagination } from "@/store/common/pagination";

const interestSkillsPag = createPaginationModule('INTEREST_SKILLS');

const state = () => ({
    interestSkills: [],
    note: "",
    pagination: defaultPagination(),
    loading: false,
    error: null,
});

const mutations = {
    ...interestSkillsPag.mutations,

    SET_INTEREST_SKILLS(state, payload) {
        state.interestSkills = payload;
    },
    SET_NOTE(state, payload) {
        state.note = payload;
    },
    SET_LOADING(state, value) {
        state.loading = value;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
};

const actions = {
    // fetch interest skills
    async fetchInterestSkills({ commit }, { personId, locationId, search = "", page = 1, pageSize = 10 }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const { data } = await api.get(
                `/manage-user/person-interest-skills?locationid=${locationId}&personid=${personId}&page_size=${pageSize}&page_number=${page}&search=${encodeURIComponent(
                    search
                )}`
            );

            const skills = data?.data ?? [];
            const note = data?.note ?? "";
            const pagination = data?.pagination ?? {};

            commit("SET_INTEREST_SKILLS", skills);
            commit("SET_NOTE", note);
            commit(interestSkillsPag.SET_PAGINATION, {
                currentPage: pagination.current_page,
                totalPages: pagination.total_pages,
                totalRecords: pagination.total_records,
                pageSize: pagination.page_size,
                hasPrevious: pagination.has_previous,
                hasNext: pagination.has_next,
            });

            return skills;
        } catch (error) {
            const message = handleApiError
                ? handleApiError(error, "Failed to fetch interest skills")
                : error.message || "Failed to fetch interest skills";
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    // save (assign) an interest skill → /manageuserajax
    async savePersonSkill({ commit }, { personId, skillId, locationId }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const { data } = await api.post(
                "/manageuserajax",
                {
                    cmd: "savepersonskill",
                    personid: personId,
                    skillid: skillId,
                    locationid: locationId,
                },
                { headers: { "Content-Type": "application/json" } }
            );

            return data;
        } catch (error) {
            const message = handleApiError
                ? handleApiError(error, "Failed to save person skill")
                : error.message || "Failed to save person skill";
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },
};

const getters = {
    allInterestSkills: (state) => state.interestSkills,
    interestSkillsNote: (state) => state.note,
    isLoadingInterestSkills: (state) => state.loading,
    interestSkillsError: (state) => state.error,
    personInterestSkillsPagination: (state) => state.pagination,
};

export default {
    state,
    mutations,
    actions,
    getters,
};
